/**
 * Página do Flow Builder
 * Editor visual de fluxos com sidebar de blocos, canvas e toolbar
 * 
 * PERMISSÕES GRANULARES:
 * - canEdit('flows'): editar nodes, salvar, publicar, configurar gatilhos
 * - canDelete('flows'): excluir nodes do fluxo
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createLogger } from '@/lib/logger';

// Logger para o Flow Builder (só aparece em desenvolvimento)
const flowLogger = createLogger('FlowBuilder');
import { useParams } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { FlowCanvas } from '@/components/tenant/flows/FlowCanvas';
import { FlowSidebar } from '@/components/tenant/flows/FlowSidebar';
import { FlowToolbar } from '@/components/tenant/flows/FlowToolbar';
import { FlowNode } from '@/types/flows';
import { ConfigureMessageDialog } from '@/components/tenant/flows/config/ConfigureMessageDialog';
import { ConfigureQuestionDialog } from '@/components/tenant/flows/config/ConfigureQuestionDialog';
import { ConfigureInputDialog } from '@/components/tenant/flows/config/ConfigureInputDialog';
import { ConfigureConditionDialog } from '@/components/tenant/flows/config/ConfigureConditionDialog';
import { ConfigureDelayDialog } from '@/components/tenant/flows/config/ConfigureDelayDialog';
import { ConfigureActionDialog } from '@/components/tenant/flows/config/ConfigureActionDialog';
import { ConfigureAssignPhaseDialog } from '@/components/tenant/flows/config/ConfigureAssignPhaseDialog';
import { ConfigureSetVariableDialog } from '@/components/tenant/flows/config/ConfigureSetVariableDialog';
import { ConfigureFilterArrayDialog } from '@/components/tenant/flows/config/ConfigureFilterArrayDialog';
import { ConfigureFormatArrayDialog } from '@/components/tenant/flows/config/ConfigureFormatArrayDialog';
import { ConfigureWebhookDialog } from '@/components/tenant/flows/config/ConfigureWebhookDialog';
import { ConfigureTriggersDialog } from '@/components/tenant/flows/config/ConfigureTriggersDialog';
import { ConfigureGotoDialog } from '@/components/tenant/flows/config/ConfigureGotoDialog';
import { ConfigureABTestDialog } from '@/components/tenant/flows/config/ConfigureABTestDialog';
import { ConfigureTriggerCheckDialog } from '@/components/tenant/flows/config/ConfigureTriggerCheckDialog';
import { ConfigurePauseBotDialog } from '@/components/tenant/flows/config/ConfigurePauseBotDialog';
import { ConfigureResumeBotDialog } from '@/components/tenant/flows/config/ConfigureResumeBotDialog';
import { ConfigureCrmCreateDealDialog } from '@/components/tenant/flows/config/ConfigureCrmCreateDealDialog';
import { ConfigureTaskCreateDialog } from '@/components/tenant/flows/config/ConfigureTaskCreateDialog';
import { ConfigureTaskChangePhaseDialog } from '@/components/tenant/flows/config/ConfigureTaskChangePhaseDialog';
import { ConfigureTaskAssignDialog } from '@/components/tenant/flows/config/ConfigureTaskAssignDialog';
import { ConfigureCrmChangePhaseDialog } from '@/components/tenant/flows/config/ConfigureCrmChangePhaseDialog';
import { ConfigureCrmAssignSellerDialog } from '@/components/tenant/flows/config/ConfigureCrmAssignSellerDialog';
import { ConfigureCrmSchedulingLinkDialog } from '@/components/tenant/flows/config/ConfigureCrmSchedulingLinkDialog';
import { ConfigureCrmCreateActivityDialog } from '@/components/tenant/flows/config/ConfigureCrmCreateActivityDialog';
import { ConfigureRemoveTagDialog } from '@/components/tenant/flows/config/ConfigureRemoveTagDialog';
import { ConfigureAddTagDialog } from '@/components/tenant/flows/config/ConfigureAddTagDialog';
import { ConfigureBusinessHoursDialog } from '@/components/tenant/flows/config/ConfigureBusinessHoursDialog';
import { ConfigureInstagramMessageDialog } from '@/components/tenant/flows/config/ConfigureInstagramMessageDialog';
import { ConfigureTemplateDialog } from '@/components/tenant/flows/config/ConfigureTemplateDialog';
import { ConfigureCarouselDialog } from '@/components/tenant/flows/config/ConfigureCarouselDialog';
import { ConfigureButtonLinkDialog } from '@/components/tenant/flows/config/ConfigureButtonLinkDialog';
import { ConfigureGTMDialog } from '@/components/tenant/flows/config/ConfigureGTMDialog';
import { ConfigureMetaPixelDialog } from '@/components/tenant/flows/config/ConfigureMetaPixelDialog';
import { ConfigureGA4Dialog } from '@/components/tenant/flows/config/ConfigureGA4Dialog';
import { ConfigureEmbedDialog } from '@/components/tenant/flows/config/ConfigureEmbedDialog';
import { ConfigureNotificationDialog } from '@/components/tenant/flows/config/ConfigureNotificationDialog';
import { ConfigureContractGenerateDialog } from '@/components/tenant/flows/config/ConfigureContractGenerateDialog';
import { ConfigureConnectionDialog } from '@/components/tenant/flows/config/ConfigureConnectionDialog';
import { ConfigureChargeGenerateDialog } from '@/components/tenant/flows/config/ConfigureChargeGenerateDialog';
import { ConfigureEndFlowDialog } from '@/components/tenant/flows/config/ConfigureEndFlowDialog';
import { FlowTestChat } from '@/components/tenant/flows/FlowTestChat';
import { FlowExecutionHistory } from '@/components/tenant/flows/FlowExecutionHistory';
import { MediaGalleryDialog } from '@/components/tenant/media/MediaGalleryDialog';
import { useFlowBuilder } from '@/hooks/useFlowBuilder';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useABTestStats } from '@/hooks/useABTestStats';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

/**
 * Página do construtor visual de fluxos
 */
export default function FlowBuilder() {
  const { flowId } = useParams<{ flowId: string }>();
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { canEdit, canDelete } = usePermissions();
  const tenantId = currentTenant?.id || '';
  
  // Permissões granulares para fluxos
  const canEditFlow = canEdit('flows');
  const canDeleteFlow = canDelete('flows');
  
  // Hook principal que gerencia todo o estado do flow
  const {
    flowName,
    flowStatus,
    nodes,
    edges,
    triggerConfig,
    isLoading,
    isSaving,
    hasChanges,
    invalidNodeIds,        // IDs de nodes com erro de validação na publicação
    saveFlow,
    publishFlow,
    pauseFlow,
    archiveFlow,
    saveTriggerConfig,
    updateNodes,
    updateEdges,
    updateNodeConfig,
    updateFlowName,
    clearInvalidNodes,     // Limpa o destaque de nodes inválidos
  } = useFlowBuilder({ flowId });
  
  // Hook para estatísticas de Teste A/B
  const { stats: abTestStats } = useABTestStats(flowId);
  
  // Estado para controlar modais de configuração
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [triggersDialogOpen, setTriggersDialogOpen] = useState(false);
  const [testPanelOpen, setTestPanelOpen] = useState(false);
  const [testStartNodeId, setTestStartNodeId] = useState<string | null>(null);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  
  // Indica se algum modal/dialog está aberto (desativa zoom/pan do canvas)
  const isAnyDialogOpen = configDialogOpen || triggersDialogOpen || galleryDialogOpen || testPanelOpen || historyPanelOpen;

  /**
   * Callback quando node é clicado (apenas seleciona, não abre modal)
   */
  const handleNodeClick = (event: React.MouseEvent, node: FlowNode) => {
    // Apenas seleciona o node - a edição é feita via botão Eye na toolbar
  };

  /**
   * Callback para abrir modal de edição (via toolbar)
   * Usa useCallback para estabilizar referência e evitar rerenders desnecessários
   */
  const handleNodeEdit = useCallback((nodeId: string, nodeType: string) => {
    if (nodeType === 'start' || nodeType === 'end') {
      return;
    }
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setSelectedNode(node);
      setConfigDialogOpen(true);
    }
  }, [nodes]);

  /**
   * Callback para excluir node (via toolbar)
   * Usa useCallback para estabilizar referência e garantir funcionamento correto
   */
  const handleNodeDelete = useCallback((nodeId: string) => {
    const nodeToDelete = nodes.find(n => n.id === nodeId);
    if (!nodeToDelete || nodeToDelete.data.type === 'start' || nodeToDelete.data.type === 'end') {
      return;
    }
    // Remove o node da lista
    const newNodes = nodes.filter(n => n.id !== nodeId);
    // Remove edges conectadas ao node
    const newEdges = edges.filter(e => e.source !== nodeId && e.target !== nodeId);
    updateNodes(newNodes);
    updateEdges(newEdges);
  }, [nodes, edges, updateNodes, updateEdges]);

  /**
   * Testar fluxo (a partir do início)
   */
  const handleTest = () => {
    setTestStartNodeId(null);
    setTestPanelOpen(true);
  };

  /**
   * Testar fluxo a partir de um node específico
   * Chamado via toolbar do node (botão Play)
   */
  const handleTestFromNode = useCallback((nodeId: string) => {
    setTestStartNodeId(nodeId);
    setTestPanelOpen(true);
  }, []);

  /**
   * Ver histórico de execuções
   */
  const handleViewHistory = () => {
    setHistoryPanelOpen(true);
  };

  /**
   * Abrir galeria de mídia
   */
  const handleOpenGallery = () => {
    setGalleryDialogOpen(true);
  };

  /**
   * Mudar status do fluxo
   */
  const handleStatusChange = async (status: 'active' | 'paused' | 'archived') => {
    if (status === 'active') {
      await publishFlow();
    } else if (status === 'paused') {
      await pauseFlow();
    } else if (status === 'archived') {
      await archiveFlow();
    }
  };

  /**
   * Abrir configurações do fluxo (gatilhos)
   */
  const handleSettingsClick = () => {
    setTriggersDialogOpen(true);
  };

  // Mostra loader enquanto carrega flow existente
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Carregando flow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Toolbar com permissões */}
      <FlowToolbar
        flowName={flowName}
        flowStatus={flowStatus}
        isSaving={isSaving}
        hasChanges={hasChanges}
        canEditFlow={canEditFlow}
        onSave={saveFlow}
        onPublish={publishFlow}
        onTest={handleTest}
        onViewHistory={handleViewHistory}
        onOpenGallery={handleOpenGallery}
        onStatusChange={handleStatusChange}
        onSettingsClick={handleSettingsClick}
        onNameChange={updateFlowName}
      />

      {/* Conteúdo: Sidebar + Canvas */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar só permite arrastar se tem permissão de edição */}
        <FlowSidebar disabled={!canEditFlow} />
        
        <ReactFlowProvider>
          <FlowCanvas
            initialNodes={nodes}
            initialEdges={edges}
            onNodesChange={updateNodes}
            onEdgesChange={updateEdges}
            onNodeClick={handleNodeClick}
            onNodeEdit={canEditFlow ? handleNodeEdit : undefined}
            onNodeDelete={canDeleteFlow ? handleNodeDelete : undefined}
            highlightedNodeId={highlightedNodeId}
            invalidNodeIds={invalidNodeIds}
            onClearInvalidNodes={clearInvalidNodes}
            abTestStats={abTestStats}
            isDialogOpen={isAnyDialogOpen}
            canEditFlow={canEditFlow}
            canDeleteFlow={canDeleteFlow}
            onTestFromNode={handleTestFromNode}
          />
        </ReactFlowProvider>
      </div>

      {/* Painel de teste de fluxo */}
      <FlowTestChat
        open={testPanelOpen}
        onOpenChange={(open) => {
          setTestPanelOpen(open);
          if (!open) setTestStartNodeId(null);
        }}
        nodes={nodes}
        edges={edges}
        onHighlightNode={setHighlightedNodeId}
        startFromNodeId={testStartNodeId}
      />

      {/* Painel de histórico de execuções */}
      <Sheet open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle>Histórico de Execuções</SheetTitle>
                <SheetDescription>
                  Visualize todas as execuções do fluxo com detalhes de cada passo
                </SheetDescription>
              </div>
              {/* Botão para recarregar o histórico de execuções */}
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['flow-executions', flowId] })}
                title="Atualizar histórico"
              >
                <RefreshCw />
              </Button>
            </div>
          </SheetHeader>
          <div className="mt-6">
            {flowId && <FlowExecutionHistory flowId={flowId} />}
          </div>
        </SheetContent>
      </Sheet>

      {/* Modais de configuração - renderizam baseado no tipo de node selecionado */}
      {(selectedNode?.data.type === 'message_text' || selectedNode?.data.type === 'message_media') && (
        <ConfigureMessageDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {(selectedNode?.data.type === 'question_buttons' || selectedNode?.data.type === 'question_list') && (
        <ConfigureQuestionDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          nodes={nodes}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {selectedNode?.data.type === 'input_data' && (
        <ConfigureInputDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {selectedNode?.data.type === 'condition' && (
        <ConfigureConditionDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {selectedNode?.data.type === 'delay' && (
        <ConfigureDelayDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'add_tag' && (
        <ConfigureAddTagDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {(selectedNode?.data.type === 'update_contact' || 
        selectedNode?.data.type === 'transfer_human' || 
        selectedNode?.data.type === 'transfer_department') && (
        <ConfigureActionDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'assign_phase' && (
        <ConfigureAssignPhaseDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'webhook' && (
        <ConfigureWebhookDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {selectedNode?.data.type === 'set_variable' && (
        <ConfigureSetVariableDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'filter_array' && (
        <ConfigureFilterArrayDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'format_array' && (
        <ConfigureFormatArrayDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'goto' && (
        <ConfigureGotoDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          allNodes={nodes}
        />
      )}

      {selectedNode?.data.type === 'ab_test' && (
        <ConfigureABTestDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'trigger_check' && (
        <ConfigureTriggerCheckDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialogs de Human Takeover (Pausar/Reativar Bot) */}
      {selectedNode?.data.type === 'pause_bot' && (
        <ConfigurePauseBotDialog
          tenantId={tenantId}
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'resume_bot' && (
        <ConfigureResumeBotDialog
          tenantId={tenantId}
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'crm_create_deal' && (
        <ConfigureCrmCreateDealDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog do node Criar Tarefa (task_create) — RELEASE 1.0.0 - 2026-05-31 */}
      {selectedNode?.data.type === 'task_create' && (
        <ConfigureTaskCreateDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog do node Alterar Fase da Tarefa (task_change_phase) — RELEASE 1.0.0 - 2026-05-31 */}
      {selectedNode?.data.type === 'task_change_phase' && (
        <ConfigureTaskChangePhaseDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog do node Atribuir Responsável da Tarefa (task_assign) — RELEASE 1.0.0 - 2026-05-31 */}
      {selectedNode?.data.type === 'task_assign' && (
        <ConfigureTaskAssignDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'crm_change_phase' && (
        <ConfigureCrmChangePhaseDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de atribuição de vendedor CRM */}
      {selectedNode?.data.type === 'crm_assign_seller' && (
        <ConfigureCrmAssignSellerDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de link de agendamento online CRM */}
      {selectedNode?.data.type === 'crm_scheduling_link' && (
        <ConfigureCrmSchedulingLinkDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog do node Criar Atividade no Negócio (crm_create_activity) — RELEASE 1.0.0 - 2026-05-31 */}
      {selectedNode?.data.type === 'crm_create_activity' && (
        <ConfigureCrmCreateActivityDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de Botão Link (CTA clicável) */}
      {selectedNode?.data.type === 'send_button_link' && (
        <ConfigureButtonLinkDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {/* Dialog de remoção de tags (contato e deal) */}
      {selectedNode?.data.type === 'remove_tag' && (
        <ConfigureRemoveTagDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de horário comercial */}
      {selectedNode?.data.type === 'business_hours' && (
        <ConfigureBusinessHoursDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de mensagem Instagram */}
      {selectedNode?.data.type === 'instagram_message' && (
        <ConfigureInstagramMessageDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {/* Dialog de template WhatsApp Oficial */}
      {selectedNode?.data.type === 'message_template' && (
        <ConfigureTemplateDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          tenantId={tenantId}
          initialConfig={selectedNode.data.config}
          onSave={(config) => updateNodeConfig(selectedNode.id, config)}
          nodeId={selectedNode.id}
          defaultNickname={selectedNode.data.config?.nickname}
        />
      )}

      {/* Dialog de carrossel multi-provider */}
      {selectedNode?.data.type === 'message_carousel' && (
        <ConfigureCarouselDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {/* Dialogs de rastreamento (GTM, Meta Pixel, GA4) */}
      {selectedNode?.data.type === 'google_tag_manager' && (
        <ConfigureGTMDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'meta_pixel' && (
        <ConfigureMetaPixelDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {selectedNode?.data.type === 'google_analytics' && (
        <ConfigureGA4Dialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de incorporação (Embed/Iframe) */}
      {selectedNode?.data.type === 'embed' && (
        <ConfigureEmbedDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de Notificação (send_notification) */}
      {selectedNode?.data.type === 'send_notification' && (
        <ConfigureNotificationDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
          tenantId={tenantId}
        />
      )}

      {/* Dialog de Geração de Contrato (contract_generate) */}
      {selectedNode?.data.type === 'contract_generate' && (
        <ConfigureContractGenerateDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog do node Conexão (connection_query) — RELEASE 1.0.0 2026-08-29 */}
      {selectedNode?.data.type === 'connection_query' && (
        <ConfigureConnectionDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode as any}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de Geração de Cobrança (charge_generate) */}
      {selectedNode?.data.type === 'charge_generate' && (
        <ConfigureChargeGenerateDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Dialog de Encerrar Fluxo (end_flow) - usado especialmente com gatilho inatividade */}
      {selectedNode?.data.type === 'end_flow' && (
        <ConfigureEndFlowDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          node={selectedNode}
          onSave={updateNodeConfig}
        />
      )}

      {/* Modal de configuração de gatilhos */}
      <ConfigureTriggersDialog
        open={triggersDialogOpen}
        onOpenChange={setTriggersDialogOpen}
        triggers={triggerConfig}
        onSave={async (triggers) => {
          const success = await saveTriggerConfig(triggers);
          if (success) {
            setTriggersDialogOpen(false);
          }
        }}
      />

      {/* Modal de galeria de mídia */}
      <MediaGalleryDialog
        open={galleryDialogOpen}
        onOpenChange={setGalleryDialogOpen}
        selectionMode={false}
      />
    </div>
  );
}
